import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"

import * as Commands from "../../../../../model/commands"
import { ID } from "../../../../../model/types"
import IdeaBox from "../../../../components/idea_box"
import { useNana } from "../../../../hooks/useNana"
import { ProjectsPageState } from "../../projects_page"
import MoveCard, {
  CardMeta,
  CardPrimaryButton,
  CardSecondaryButton,
  previewFromHtml,
} from "../move_card"

export default function ProjectCard({
  projectId,
  onReroll,
  onObjectGone,
  isNewInvitation = false,
}: {
  projectId: ID
  onReroll: () => void
  onObjectGone: () => void
  isNewInvitation?: boolean
}) {
  const { model, commander } = useNana()
  const navigate = useNavigate()
  const project = isNewInvitation ? null : model.getProject(projectId)

  useEffect(() => {
    if (!isNewInvitation && !project) onObjectGone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, isNewInvitation])

  if (isNewInvitation) {
    const openProjects = () => {
      ProjectsPageState.openProjectId = null
      ProjectsPageState.lastSelectedTab = "in_progress"
      navigate("/app/projects")
    }

    return (
      <MoveCard
        move="project"
        onReroll={onReroll}
        onPrimary={openProjects}
        showReroll={false}
        actions={
          <CardPrimaryButton onClick={openProjects}>
            Start a new project
          </CardPrimaryButton>
        }
      >
        <div className="flex min-h-[6.5rem] flex-1 flex-col justify-center gap-2">
          <h3 className="text-[1.2rem] font-semibold tracking-[-0.02em] text-stone-900 dark:text-stone-50">
            Start a new project
          </h3>
          <p className="text-[14px] leading-relaxed text-stone-500 dark:text-stone-400">
            Assemble ideas into an outline when something wants to become a piece of
            writing.
          </p>
          <CardMeta>
            <span className="opacity-70">No projects in progress</span>
          </CardMeta>
        </div>
      </MoveCard>
    )
  }

  if (!project) return null

  const open = () => {
    ProjectsPageState.openProjectId = project.id
    ProjectsPageState.lastSelectedTab = "in_progress"
    navigate("/app/projects")
  }

  const markDone = () => {
    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: project.id,
        fields: {
          status: "completed",
        },
      }),
    )
    onObjectGone()
  }

  const ideas = project.ideaIds
    .map((id) => model.getIdea(id))
    .filter((i): i is NonNullable<typeof i> => i != null)
  const goal = project.goalId ? model.getIdea(project.goalId) : null

  return (
    <MoveCard
      move="project"
      onReroll={onReroll}
      onPrimary={open}
      actions={
        <>
          <CardPrimaryButton onClick={open}>Open project</CardPrimaryButton>
          <CardSecondaryButton onClick={markDone}>Mark done</CardSecondaryButton>
        </>
      }
    >
      <div className="flex min-h-[7.5rem] flex-1 flex-col gap-3">
        <div className="flex items-center gap-2.5">
          {goal && <IdeaBox idea={goal} />}
          <h3 className="line-clamp-2 text-[1.2rem] font-semibold tracking-[-0.02em] text-stone-900 dark:text-stone-50">
            {project.name || "Untitled project"}
          </h3>
        </div>
        {ideas.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {ideas.slice(0, 5).map((idea) => (
              <div
                key={String(idea.id)}
                className="flex min-w-0 items-center gap-2"
              >
                <IdeaBox idea={idea} shadow={false} />
                <span className="min-w-0 truncate text-[13px] text-stone-700 dark:text-stone-200">
                  {idea.title || previewFromHtml(idea.content, 60) || "Untitled"}
                </span>
              </div>
            ))}
            {ideas.length > 5 && (
              <span className="text-[12px] text-stone-400">
                +{ideas.length - 5} more
              </span>
            )}
          </div>
        ) : (
          <p className="text-[14px] text-stone-500 dark:text-stone-400">
            Empty outline — add ideas when ready.
          </p>
        )}
        <CardMeta>
          <span className="opacity-70">In progress</span>
        </CardMeta>
      </div>
    </MoveCard>
  )
}
